import { Baby, Plus, User as UserIcon } from 'lucide-react';

/**
 * ProfileSwitcher — pill row of profile chips + a "manage" plus-button.
 *
 * Rendered on the dashboard when the account has 2+ profiles. Emits
 * `onSwitch(id)` when a chip is tapped. `onManage()` opens the profiles
 * management screen.
 *
 * Props:
 *   profiles: Array<{id, name, is_kid, is_primary}>
 *   activeProfileId: string | null
 *   switching: boolean         disables all chips while a switch is in flight
 *   onSwitch: (id) => void
 *   onManage: () => void
 */
export default function ProfileSwitcher({ profiles, activeProfileId, switching, onSwitch, onManage }) {
  if (!profiles || profiles.length <= 1) return null;

  return (
    <div
      className="mb-4 flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1"
      data-testid="profile-switcher"
    >
      {profiles.map((p) => {
        const active = p.id === activeProfileId;
        const Icon = p.is_kid ? Baby : UserIcon;
        const chipCls = active
          ? 'bg-qc-green/15 border-qc-green text-qc-green'
          : 'bg-qc-surface border-qc-border text-qc-text-dim hover:border-qc-text-faint';
        const iconWrapCls = active
          ? 'bg-qc-green text-qc-bg'
          : p.is_kid
          ? 'bg-qc-amber/20 text-qc-amber'
          : 'bg-qc-surface2 text-qc-text-dim';
        return (
          <button
            key={p.id}
            onClick={() => onSwitch(p.id)}
            disabled={switching || active}
            data-testid={`switcher-profile-${p.id}`}
            className={`flex-shrink-0 flex items-center gap-2 h-10 pl-2 pr-4 rounded-full border-2 font-bold text-sm tappable ${chipCls}`}
          >
            <span className={`w-7 h-7 rounded-full flex items-center justify-center ${iconWrapCls}`}>
              <Icon className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <span className="truncate max-w-[110px]">{p.name || (p.is_primary ? 'You' : 'Profile')}</span>
          </button>
        );
      })}
      <button
        onClick={onManage}
        data-testid="switcher-manage"
        className="flex-shrink-0 h-10 w-10 rounded-full bg-qc-surface border-2 border-qc-border text-qc-text-dim flex items-center justify-center tappable"
        aria-label="Manage profiles"
      >
        <Plus className="w-5 h-5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
